// Pure math + parse helpers for the activity dock's position state.
// Extracted from `useActivityDockPosition.ts` so the hook stays under the
// file-size hygiene cap and so the math can be unit-tested without
// touching React or localStorage.
//
// Phase 4 (docs/planning/org_activity_floating_dock.md §12):
// - `DockEdge` now supports all four edges (left/right/top/bottom).
// - `offsetRatio` is axis-aware: for left/right it's the VERTICAL ratio,
//   for top/bottom it's the HORIZONTAL ratio. The tab geometry rotates
//   accordingly (vertical pill on the sides, horizontal pill on top/bottom).
// - Legacy stored values (left/right only) keep working unchanged; values
//   with bogus edges fall back to the default.

import { FIREFLY_DOCK_HIT_DIAMETER_PX } from "./firefly/fireflyDockGeometry";

export const DOCK_POSITION_STORAGE_KEY = "bb-activity-dock-position";

export type DockEdge = "left" | "right" | "top" | "bottom";

export type ActivityDockPosition = {
  edge: DockEdge;
  /**
   * 0..1 along the secondary axis of the snapped edge.
   *   - edge ∈ {"left", "right"}: vertical ratio (0=top, 1=bottom).
   *   - edge ∈ {"top", "bottom"}: horizontal ratio (0=left, 1=right).
   */
  offsetRatio: number;
};

export const DEFAULT_POSITION: ActivityDockPosition = {
  edge: "right",
  offsetRatio: 0.4,
};

/** Side-tab dimensions (left/right). Vertical pill. */
export const SIDE_TAB_WIDTH_PX = 28;
export const SIDE_TAB_HEIGHT_PX = 108;

/** Top/bottom-tab dimensions. Horizontal pill = side dims swapped. */
export const HEDGE_TAB_WIDTH_PX = SIDE_TAB_HEIGHT_PX;
export const HEDGE_TAB_HEIGHT_PX = SIDE_TAB_WIDTH_PX;

/** True when the edge is a horizontal viewport edge (top or bottom). */
export function isHorizontalEdge(edge: DockEdge): boolean {
  return edge === "top" || edge === "bottom";
}

/**
 * Map a raw coord (axis-aligned to the snapped edge) to a 0..1 ratio.
 * - For top/bottom: pass the X coord with viewportW + tab WIDTH.
 * - For left/right: pass the Y coord with viewportH + tab HEIGHT.
 * Degrades to the default ratio when the viewport range is zero.
 */
export function clampOffsetRatio(
  coord: number,
  viewportRange: number,
  tabSizeAlongAxis: number,
): number {
  if (viewportRange <= 0) return DEFAULT_POSITION.offsetRatio;
  const maxCoord = Math.max(0, viewportRange - tabSizeAlongAxis);
  const clamped = Math.min(Math.max(coord, 0), maxCoord);
  return maxCoord === 0 ? 0 : clamped / maxCoord;
}

/**
 * Pick the nearest of the four viewport edges for a drop point (x, y).
 * Manhattan distance to each edge; ties prefer the side edges
 * (left/right) since they were the original behavior and aspect ratios
 * tend to favor them.
 */
export function snapToNearestEdge(
  x: number,
  y: number,
  viewportW: number,
  viewportH: number,
): DockEdge {
  if (viewportW <= 0 || viewportH <= 0) return DEFAULT_POSITION.edge;
  const dLeft = Math.max(0, x);
  const dRight = Math.max(0, viewportW - x);
  const dTop = Math.max(0, y);
  const dBottom = Math.max(0, viewportH - y);
  // Find the minimum. Tie-break order: right, left, bottom, top (side first).
  const sideMin = Math.min(dLeft, dRight);
  const vertMin = Math.min(dTop, dBottom);
  if (sideMin <= vertMin) {
    return dRight <= dLeft ? "right" : "left";
  }
  return dBottom <= dTop ? "bottom" : "top";
}

/**
 * Convert a pointer drop coordinate into the persisted shape, picking the
 * nearest edge and computing the correct axis offset.
 */
export function positionFromCoords(args: {
  x: number;
  y: number;
  viewportW: number;
  viewportH: number;
}): ActivityDockPosition {
  const { x, y, viewportW, viewportH } = args;
  const edge = snapToNearestEdge(x, y, viewportW, viewportH);
  const offsetRatio = isHorizontalEdge(edge)
    ? clampOffsetRatio(x, viewportW, FIREFLY_DOCK_HIT_DIAMETER_PX)
    : clampOffsetRatio(y, viewportH, FIREFLY_DOCK_HIT_DIAMETER_PX);
  return { edge, offsetRatio };
}

/**
 * Re-clamp an existing position after a viewport resize. The edge stays
 * put; only the ratio is normalized into [0, 1] (defensive, since stored
 * values are already in-range).
 */
export function clampPositionToViewport(pos: ActivityDockPosition): ActivityDockPosition {
  return {
    edge: pos.edge,
    offsetRatio: Math.min(Math.max(pos.offsetRatio, 0), 1),
  };
}

/**
 * Viewport center of the Firefly tab for a persisted edge + offsetRatio.
 * Used when the floating tab is unmounted (sidebar-docked) but undock
 * travel still needs an aim point.
 */
export function centerFromDockPosition(
  pos: ActivityDockPosition,
  viewportW: number,
  viewportH: number,
  tabSizePx = FIREFLY_DOCK_HIT_DIAMETER_PX,
  sidebarW = 0,
): { x: number; y: number } {
  const ratio = Math.min(Math.max(pos.offsetRatio, 0), 1);
  const half = tabSizePx / 2;
  if (pos.edge === "left") {
    return { x: sidebarW + half, y: (viewportH - tabSizePx) * ratio + half };
  }
  if (pos.edge === "right") {
    return { x: viewportW - half, y: (viewportH - tabSizePx) * ratio + half };
  }
  if (pos.edge === "top") {
    const usable = Math.max(0, viewportW - sidebarW - tabSizePx);
    return { x: sidebarW + usable * ratio + half, y: half };
  }
  const usable = Math.max(0, viewportW - sidebarW - tabSizePx);
  return { x: sidebarW + usable * ratio + half, y: viewportH - half };
}

/**
 * Parse a stored localStorage blob. Accepts both the new 4-edge shape and
 * the legacy 2-edge shape (where `edge` was only "left" | "right"). Any
 * other input returns null so callers fall back to the default.
 */
export function parseStoredPosition(raw: string | null): ActivityDockPosition | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ActivityDockPosition>;
    if (
      parsed.edge !== "left" &&
      parsed.edge !== "right" &&
      parsed.edge !== "top" &&
      parsed.edge !== "bottom"
    ) {
      return null;
    }
    if (typeof parsed.offsetRatio !== "number" || Number.isNaN(parsed.offsetRatio)) {
      return null;
    }
    const clamped = Math.min(Math.max(parsed.offsetRatio, 0), 1);
    return { edge: parsed.edge, offsetRatio: clamped };
  } catch {
    return null;
  }
}

/**
 * Pure detector for an on-screen keyboard. Compares the document's logical
 * height (`window.innerHeight`) against the `visualViewport.height` —
 * a substantial drop indicates a virtual keyboard is open.
 * The 150px threshold matches docs/planning §12's locked specification.
 */
export const KEYBOARD_OPEN_THRESHOLD_PX = 150;

export function detectKeyboardOpen(
  windowInnerHeight: number,
  visualViewportHeight: number,
): boolean {
  if (windowInnerHeight <= 0 || visualViewportHeight <= 0) return false;
  return windowInnerHeight - visualViewportHeight > KEYBOARD_OPEN_THRESHOLD_PX;
}
